import { equals, lte, gte, includes, isNil, prop } from 'ramda';

// Largest dimension (px) allowed before downscaling.
// Document pages are 794×1123px at 96 DPI in portrait (1123×794 in landscape),
// so the largest page dimension is 1123px; 2246 = 2 × 1123 keeps margin for print sharpness.
const MAX_DIMENSION = 2246;
// Quality used when re-encoding JPEGs.
const JPEG_QUALITY = 0.8;
const MIMETYPE_JPEG = 'image/jpeg';
const MIMETYPE_PNG = 'image/png';
const SUPPORTED_MIMETYPES = [MIMETYPE_JPEG, MIMETYPE_PNG];

const loadImageFromUrl = (objectUrl) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Lecture image échouée'));
  img.src = objectUrl;
});

const computeTargetDimensions = (width, height) => {
  const largestDimension = Math.max(width, height);
  if (lte(largestDimension, MAX_DIMENSION)) return { width, height };
  const ratio = MAX_DIMENSION / largestDimension;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
};

const canvasToBlob = (canvas, mimetype, quality) => new Promise((resolve) => {
  canvas.toBlob(resolve, mimetype, quality);
});

// Compresses an image File/Blob via <canvas> (no third-party dependency).
// PDFs and unsupported mimetypes are returned untouched. The original file is
// returned whenever the compressed result is not actually lighter.
export const compressImage = async (file) => {
  const mimetype = prop('type', file);
  if (!includes(mimetype, SUPPORTED_MIMETYPES)) return file;

  let objectUrl;
  try {
    objectUrl = URL.createObjectURL(file);
    const img = await loadImageFromUrl(objectUrl);
    const { width, height } = computeTargetDimensions(img.naturalWidth, img.naturalHeight);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);

    const isJpeg = equals(MIMETYPE_JPEG, mimetype);
    const outputMimetype = isJpeg ? MIMETYPE_JPEG : MIMETYPE_PNG;
    const quality = isJpeg ? JPEG_QUALITY : undefined;
    const blob = await canvasToBlob(canvas, outputMimetype, quality);

    if (isNil(blob) || gte(prop('size', blob), prop('size', file))) return file;

    return new File([blob], prop('name', file), { type: outputMimetype });
  } catch (_) {
    return file;
  } finally {
    if (!isNil(objectUrl)) URL.revokeObjectURL(objectUrl);
  }
};
